import { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiBase } from '../apiBase';
import { canEditDetails, getUsername } from '../auth';
import { useBagProducts } from './BagProductsContext';
import {
  LoadingSpinner,
  TableFiltersBar,
  TablePaginationBar,
  filterControl,
  filterLabel,
  filterLabelNarrow,
  inDateRange,
  mobileCardList,
  MobileRowCard,
  rowMatchesQuery,
  scrollTableWrap,
  stickyFirstTd,
  stickyFirstTh,
  stickyThead,
  useTablePagination,
  modalPanelClass,
} from './tableToolbar';
import RowDetailModal, { detailRowAttrs } from './RowDetailModal';
import { formatBrandLabel } from './brandTheme';
import { buildCustomerInvoiceRows } from './pendingBills';
import { downloadPromotionRules, downloadSpecialPromotions } from './promotionsExport';

const apiBase = getApiBase();

const PROMO_TYPES = [
  { id: 'free_bags', label: 'Free bag issue' },
  { id: 'invoice_discount', label: 'Invoice discount' },
  { id: 'target_promotion', label: 'Target promotion' },
];

const PAGE_TABS = [
  { id: 'special', label: 'Special Promotions' },
  { id: 'rules', label: 'Promotion Rules' },
];

function downloadContentSlug(scopeId) {
  if (scopeId === 'free_bags') return 'free-bag-issue';
  if (scopeId === 'invoice_discount') return 'invoice-discount';
  if (scopeId === 'target_promotion') return 'target-promotion';
  return 'all-types';
}

function emptyRuleForm(brands) {
  const cashbacks = {};
  for (const b of brands) {
    cashbacks[b.key] = '';
  }
  return {
    customerId: '',
    startDate: '',
    endDate: '',
    cashbacks,
  };
}

function formFromRule(rule, brands) {
  const stored = rule?.cashbacks && typeof rule.cashbacks === 'object' ? rule.cashbacks : {};
  const cashbacks = {};
  for (const b of brands) {
    const v = stored[b.key];
    cashbacks[b.key] = v != null && v !== '' ? String(v) : '';
  }
  return {
    customerId: rule?.customerId || '',
    startDate: rule?.startDate || '',
    endDate: rule?.endDate || '',
    cashbacks,
  };
}

function ruleCashbackEntries(row, brands) {
  const stored = row?.cashbacks && typeof row.cashbacks === 'object' ? row.cashbacks : {};
  const seen = new Set();
  const entries = [];
  for (const b of brands) {
    seen.add(b.key);
    const amount = Number(stored[b.key]) || 0;
    if (amount > 0) {
      entries.push({ key: b.key, label: formatBrandLabel(b) || b.label, amount });
    }
  }
  for (const [key, value] of Object.entries(stored)) {
    if (seen.has(key)) continue;
    const amount = Number(value) || 0;
    if (amount > 0) entries.push({ key, label: key, amount });
  }
  return entries;
}

function withRuleRowSpans(items) {
  const counts = new Map();
  for (const item of items) {
    const id = item.rule?.id;
    if (!id) continue;
    counts.set(id, (counts.get(id) || 0) + 1);
  }
  const seen = new Set();
  return items.map((item) => {
    const id = item.rule?.id;
    const first = Boolean(id) && !seen.has(id);
    if (first) seen.add(id);
    return { ...item, ruleRowSpan: first ? counts.get(id) : 0 };
  });
}

function rulePeriodOverlaps(start, end, from, to) {
  if (!from && !to) return true;
  const s = String(start ?? '');
  const e = String(end ?? '');
  if (from && e && e < from) return false;
  if (to && s && s > to) return false;
  return true;
}

function promoType(row) {
  const t = String(row?.type ?? '').trim();
  if (t === 'invoice_discount' || t === 'target_promotion' || t === 'rule_cashback') return t;
  return 'free_bags';
}

function promoTypeLabel(type) {
  return PROMO_TYPES.find((t) => t.id === type)?.label ?? 'Free bag issue';
}

function promoAmount(row) {
  const type = promoType(row);
  if (type === 'invoice_discount' || type === 'target_promotion') {
    return Number(row.discountAmount) || 0;
  }
  return 0;
}

function normalizeCustomerName(s) {
  return String(s ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function money(n) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'LKR',
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);
}

function totalFreeBags(row, brands) {
  return brands.reduce((s, b) => s + (Number(row[`${b.key}Bags`]) || 0), 0);
}

function requestedBags(value) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
}

/** @returns {string[]} user-facing errors; empty if valid */
function validatePromotionAgainstStock(form, stockByBrand, bagBrands) {
  const issues = [];
  let anyRequested = false;

  for (const b of bagBrands) {
    const requested = requestedBags(form[`${b.key}Bags`]);
    if (requested <= 0) continue;
    anyRequested = true;
    const available = stockByBrand[b.key] ?? 0;
    if (available <= 0) {
      issues.push(`${b.label} is out of stock — you cannot issue free bags.`);
    } else if (requested > available) {
      issues.push(
        `${b.label}: only ${available.toLocaleString()} bag${available === 1 ? '' : 's'} in stock (you entered ${requested.toLocaleString()}).`,
      );
    }
  }

  if (!anyRequested) {
    const anyInStock = bagBrands.some((b) => (stockByBrand[b.key] ?? 0) > 0);
    if (!anyInStock) {
      issues.push('No bags in stock right now — nothing can be issued.');
    }
  }

  return issues;
}

function emptyForm(brands, type = 'free_bags') {
  const f = {
    type,
    date: new Date().toISOString().slice(0, 10),
    customerId: '',
    billNumber: '',
    reason: '',
    billId: '',
    discountMode: 'whole_invoice',
    discountValue: '',
    discountAmount: '',
  };
  for (const b of brands) {
    f[`${b.key}Bags`] = '';
  }
  return f;
}

function formFromPromotion(promo, brands) {
  const type = promoType(promo);
  const billDigits = String(promo.billNumber ?? '').replace(/\D/g, '');
  const f = {
    type,
    date: promo.date || new Date().toISOString().slice(0, 10),
    customerId: promo.customerId || '',
    billNumber: billDigits ? String(parseInt(billDigits, 10)) : '',
    reason: promo.reason || '',
    billId: promo.billId || '',
    discountMode: promo.discountMode === 'per_bag' ? 'per_bag' : 'whole_invoice',
    discountValue: promo.discountValue != null ? String(promo.discountValue) : '',
    discountAmount: promo.discountAmount != null ? String(promo.discountAmount) : '',
  };
  for (const b of brands) {
    const v = promo[`${b.key}Bags`];
    f[`${b.key}Bags`] = v != null && v !== '' ? String(v) : '';
  }
  return f;
}

function totalBagsOnBill(bill, brands) {
  return brands.reduce((s, b) => s + (Number(bill[`${b.key}Bags`]) || 0), 0);
}

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function sumOtherInvoiceDiscounts(promotions, billId, excludeId) {
  const id = String(billId ?? '').trim();
  const skip = String(excludeId ?? '').trim();
  if (!id) return 0;
  let sum = 0;
  for (const row of Array.isArray(promotions) ? promotions : []) {
    if (String(row?.type ?? '').trim() !== 'invoice_discount') continue;
    if (String(row.billId ?? '').trim() !== id) continue;
    if (skip && String(row.id ?? '').trim() === skip) continue;
    sum += Number(row.discountAmount) || 0;
  }
  return roundMoney(sum);
}

function computePreviewDiscount(form, selectedBill, brands) {
  const value = Number(form.discountValue);
  if (!Number.isFinite(value) || value <= 0 || !selectedBill) return 0;
  const billTotal = Number(selectedBill.totalAmount) || 0;
  if (billTotal <= 0) return 0;
  if (form.discountMode === 'whole_invoice') {
    return Math.min(value, billTotal);
  }
  const bags = totalBagsOnBill(selectedBill, brands);
  if (bags <= 0) return 0;
  return Math.min(Math.round(value * bags * 100) / 100, billTotal);
}

function SpecialPromotionsPanel() {
  const { brands } = useBagProducts();
  const [rows, setRows] = useState([]);
  const [ledgerPromotions, setLedgerPromotions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editPromotion, setEditPromotion] = useState(null);
  const [form, setForm] = useState(() => emptyForm([]));
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [stockSummary, setStockSummary] = useState(null);
  const [stockLoading, setStockLoading] = useState(false);
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [billsLoading, setBillsLoading] = useState(false);
  const [promoTab, setPromoTab] = useState('free_bags');
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadTypes, setDownloadTypes] = useState(() => PROMO_TYPES.map((type) => type.id));

  const loadCustomers = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/api/customers`);
      if (!res.ok) throw new Error('Failed to load customers');
      const data = await res.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch {
      setCustomers([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/promotions`);
      if (!res.ok) throw new Error('Failed to load promotions');
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setLedgerPromotions(list);
      setRows(list.filter((r) => promoType(r) !== 'rule_cashback'));
    } catch (e) {
      setError(e.message || 'Could not load data');
      setLedgerPromotions([]);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const loadStock = useCallback(async () => {
    setStockLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/stocks/summary`);
      if (!res.ok) throw new Error('Failed to load stock');
      const data = await res.json();
      setStockSummary(data);
    } catch {
      setStockSummary(null);
    } finally {
      setStockLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!modalOpen) return;
    loadStock();
  }, [modalOpen, loadStock]);

  const loadBills = useCallback(async () => {
    setBillsLoading(true);
    try {
      const [billsRes, paymentsRes] = await Promise.all([
        fetch(`${apiBase}/api/bills`),
        fetch(`${apiBase}/api/payments`),
      ]);
      if (!billsRes.ok) throw new Error('Failed to load bills');
      const data = await billsRes.json();
      setBills(Array.isArray(data) ? data : []);
      if (paymentsRes.ok) {
        const payData = await paymentsRes.json();
        setPayments(Array.isArray(payData) ? payData : []);
      } else {
        setPayments([]);
      }
    } catch {
      setBills([]);
      setPayments([]);
    } finally {
      setBillsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!modalOpen) return;
    loadBills();
  }, [modalOpen, loadBills]);

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === form.customerId),
    [customers, form.customerId],
  );

  const customerBills = useMemo(() => {
    if (!selectedCustomer) return [];
    const nk = normalizeCustomerName(selectedCustomer.name);
    return bills
      .filter((b) => normalizeCustomerName(b.customerName) === nk)
      .sort((a, b) => String(b.date ?? '').localeCompare(String(a.date ?? '')));
  }, [bills, selectedCustomer]);

  const selectedBill = useMemo(
    () => customerBills.find((b) => b.id === form.billId) ?? null,
    [customerBills, form.billId],
  );

  const previewDiscount = useMemo(
    () => computePreviewDiscount(form, selectedBill, brands),
    [form, selectedBill, brands],
  );

  const customerInvoiceById = useMemo(() => {
    const map = new Map();
    if (!selectedCustomer) return map;
    for (const row of buildCustomerInvoiceRows(selectedCustomer, bills, payments, ledgerPromotions)) {
      if (row?.id) map.set(row.id, row);
    }
    return map;
  }, [selectedCustomer, bills, payments, ledgerPromotions]);

  const otherInvoiceDiscount = useMemo(
    () => sumOtherInvoiceDiscounts(ledgerPromotions, selectedBill?.id, editPromotion?.id),
    [ledgerPromotions, selectedBill, editPromotion],
  );

  const amountToPay = useMemo(() => {
    if (!selectedBill) return 0;
    const linked = customerInvoiceById.get(selectedBill.id);
    const gross = Number(selectedBill.totalAmount) || 0;
    const savedOutstanding = linked
      ? Math.max(0, roundMoney(linked.outstandingAmount))
      : Math.max(0, roundMoney(gross - otherInvoiceDiscount));
    const savedDiscountOnEdit =
      editPromotion && String(editPromotion.billId ?? '') === String(selectedBill.id)
        ? Number(editPromotion.discountAmount) || 0
        : 0;
    return Math.max(0, roundMoney(savedOutstanding + savedDiscountOnEdit - previewDiscount));
  }, [selectedBill, customerInvoiceById, otherInvoiceDiscount, previewDiscount, editPromotion]);

  const stockByBrand = useMemo(() => {
    const map = {};
    for (const b of stockSummary?.brands || []) {
      map[b.key] = Math.max(0, Math.floor(Number(b.availableForRequest ?? b.bags) || 0));
    }
    if (editPromotion) {
      for (const b of brands) {
        map[b.key] = (map[b.key] ?? 0) + (Number(editPromotion[`${b.key}Bags`]) || 0);
      }
    }
    return map;
  }, [stockSummary, editPromotion, brands]);

  const anyStockAvailable = useMemo(
    () => brands.some((b) => (stockByBrand[b.key] ?? 0) > 0),
    [stockByBrand, brands],
  );

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (!inDateRange(r.date, dateFrom, dateTo)) return false;
      if (customerFilter && r.customerId !== customerFilter) return false;
      return rowMatchesQuery(search, [
        r.date,
        r.customerName,
        r.customerId,
        r.billNumber,
        r.invoiceNumber,
        r.reason,
        r.enteredBy,
        promoTypeLabel(promoType(r)),
        String(promoAmount(r)),
        String(totalFreeBags(r, brands)),
      ]);
    });
  }, [rows, search, dateFrom, dateTo, customerFilter, brands]);

  const pagination = useTablePagination(filteredRows.length, [search, dateFrom, dateTo, customerFilter]);
  const pagedRows = useMemo(
    () => filteredRows.slice(pagination.offset, pagination.offset + pagination.pageSize),
    [filteredRows, pagination.offset, pagination.pageSize]
  );

  const openModal = () => {
    setEditPromotion(null);
    setPromoTab('free_bags');
    setForm(emptyForm(brands, 'free_bags'));
    setSaveError(null);
    loadCustomers();
    setModalOpen(true);
  };

  const openPromotionEdit = (promo) => {
    if (!promo?.id) return;
    const type = promoType(promo);
    setSaveError(null);
    loadCustomers();
    setEditPromotion(promo);
    setPromoTab(type);
    setForm(formFromPromotion(promo, brands));
    setDetailRow(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditPromotion(null);
    setPromoTab('free_bags');
    setSaveError(null);
  };

  const switchPromoTab = (tabId) => {
    if (editPromotion) return;
    setPromoTab(tabId);
    setForm((f) => ({ ...emptyForm(brands, tabId), date: f.date, customerId: f.customerId, reason: f.reason }));
    setSaveError(null);
  };

  const handleChange = (field, value) => {
    if (field === 'billNumber') {
      const digits = String(value).replace(/\D/g, '').slice(0, 3);
      setForm((f) => ({ ...f, billNumber: digits }));
      return;
    }
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const username = getUsername();
    if (!username) {
      setSaveError('You need to be signed in with a username.');
      return;
    }
    if (!form.customerId) {
      setSaveError('Select a customer.');
      return;
    }
    if (!String(form.reason || '').trim()) {
      setSaveError('Enter a reason for the promotion.');
      return;
    }

    const activeType = editPromotion ? promoType(editPromotion) : promoTab;

    if (activeType === 'free_bags') {
      const stockIssues = validatePromotionAgainstStock(form, stockByBrand, brands);
      if (stockIssues.length > 0) {
        setSaveError(stockIssues.join(' '));
        return;
      }
    } else if (activeType === 'invoice_discount') {
      if (!form.billId) {
        setSaveError('Select an invoice for the discount.');
        return;
      }
      if (previewDiscount <= 0) {
        setSaveError('Enter a valid discount amount.');
        return;
      }
    } else if (activeType === 'target_promotion') {
      const amt = Number(form.discountAmount);
      if (!Number.isFinite(amt) || amt <= 0) {
        setSaveError('Enter a promotion amount greater than zero.');
        return;
      }
    }

    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        type: activeType,
        date: form.date,
        customerId: form.customerId,
        reason: String(form.reason).trim(),
      };
      if (activeType === 'free_bags') {
        payload.billNumber = form.billNumber || undefined;
        for (const b of brands) {
          payload[`${b.key}Bags`] = form[`${b.key}Bags`];
        }
      } else if (activeType === 'invoice_discount') {
        payload.billId = form.billId;
        payload.discountMode = form.discountMode;
        payload.discountValue = form.discountValue;
      } else {
        payload.discountAmount = form.discountAmount;
      }
      const isEdit = !!editPromotion?.id;
      const res = await fetch(
        isEdit ? `${apiBase}/api/promotions/${encodeURIComponent(editPromotion.id)}` : `${apiBase}/api/promotions`,
        {
          method: isEdit ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            isEdit ? { ...payload, updatedBy: username } : { ...payload, enteredBy: username },
          ),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveError(data.error || 'Save failed');
        return;
      }
      await load();
      closeModal();
    } catch {
      setSaveError('Could not reach the server.');
    } finally {
      setSaving(false);
    }
  };

  const openDownload = () => {
    const available = PROMO_TYPES.filter((type) => filteredRows.some((row) => promoType(row) === type.id)).map(
      (type) => type.id,
    );
    setDownloadTypes(available.length > 0 ? available : PROMO_TYPES.map((type) => type.id));
    setDownloadOpen(true);
  };

  const availableDownloadTypes = PROMO_TYPES.filter((type) =>
    filteredRows.some((row) => promoType(row) === type.id),
  );
  const allDownloadTypesSelected =
    availableDownloadTypes.length > 0 &&
    availableDownloadTypes.every((type) => downloadTypes.includes(type.id));
  const downloadRows = allDownloadTypesSelected
    ? filteredRows
    : filteredRows.filter((row) => downloadTypes.includes(promoType(row)));

  const toggleDownloadType = (typeId) => {
    setDownloadTypes((current) =>
      current.includes(typeId) ? current.filter((id) => id !== typeId) : [...current, typeId],
    );
  };

  const toggleAllDownloadTypes = () => {
    setDownloadTypes(allDownloadTypesSelected ? [] : availableDownloadTypes.map((type) => type.id));
  };

  const confirmDownload = () => {
    if (downloadRows.length === 0) return;
    const selected = availableDownloadTypes.filter((type) => downloadTypes.includes(type.id));
    const contentLabel = allDownloadTypesSelected
      ? 'All types'
      : selected.map((type) => type.label).join(', ');
    const contentSlug = allDownloadTypesSelected
      ? 'all-types'
      : selected.map((type) => downloadContentSlug(type.id)).join('-');
    const customer = customers.find((c) => c.id === customerFilter)?.name || '';
    downloadSpecialPromotions(downloadRows, brands, {
      dateFrom,
      dateTo,
      customer,
      search,
      contentLabel,
      contentSlug,
    });
    setDownloadOpen(false);
  };

  const handleDelete = async (promo) => {
    if (!promo?.id) return;
    const label = promo.customerName ? ` for ${promo.customerName}` : '';
    const type = promoType(promo);
    const stockNote =
      type === 'free_bags' ? ' Live stock totals will be restored.' : ' Customer balance and cashier will be updated.';
    if (!window.confirm(`Delete this promotion${label}?${stockNote}`)) return;
    setDeletingId(promo.id);
    try {
      const res = await fetch(`${apiBase}/api/promotions/${encodeURIComponent(promo.id)}`, {
        method: 'DELETE',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.error || 'Delete failed');
        return;
      }
      setDetailRow(null);
      await load();
    } catch {
      alert('Could not reach the server.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-500">Record promotions: free bags, invoice discounts, or target rewards.</p>
        <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row">
          <button
            type="button"
            onClick={openDownload}
            disabled={loading || filteredRows.length === 0}
            className="inline-flex w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >
            Download (PDF + Excel)
          </button>
          <button
            type="button"
            onClick={openModal}
            className="inline-flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition hover:brightness-[1.03] sm:w-auto"
          >
            Add promotion
          </button>
        </div>
      </div>

      {error ? (
        <p className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-800 ring-1 ring-red-100" role="alert">
          {error}
        </p>
      ) : null}

      <TableFiltersBar
        hint={
          !loading && rows.length > 0
            ? `Showing ${filteredRows.length} of ${rows.length} promotion${rows.length === 1 ? '' : 's'}`
            : null
        }
      >
        <label className={filterLabel}>
          Search
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Customer, bill #, reason…"
            className={filterControl}
          />
        </label>
        <label className={filterLabelNarrow}>
          From date
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className={filterControl}
          />
        </label>
        <label className={filterLabelNarrow}>
          To date
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className={filterControl}
          />
        </label>
        <label className={filterLabel}>
          Customer
          <select
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            className={filterControl}
          >
            <option value="">All customers</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </TableFiltersBar>

      <div className="space-y-3">
        <div className={mobileCardList}>
          {loading ? (
            <p className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-slate-500 ring-1 ring-slate-100">
              <LoadingSpinner />
            </p>
          ) : rows.length === 0 ? (
            <p className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-slate-500 ring-1 ring-slate-100">
              No promotions yet. Use &quot;Add promotion&quot; to record free bags for a customer.
            </p>
          ) : filteredRows.length === 0 ? (
            <p className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-slate-500 ring-1 ring-slate-100">
              No rows match your search or filters.
            </p>
          ) : (
            pagedRows.map((r) => (
              <MobileRowCard
                key={r.id}
                title={r.customerName || '—'}
                subtitle={[r.customerId ? `ID ${r.customerId}` : '', r.date].filter(Boolean).join(' · ')}
                onClick={() => setDetailRow(r)}
                fields={[
                  { label: 'Type', value: promoTypeLabel(promoType(r)) },
                  { label: 'Bill / Invoice', value: r.invoiceNumber || (r.billNumber ? `#${r.billNumber}` : '—') },
                  { label: 'Amount', value: money(promoAmount(r)) },
                  { label: 'Free bags', value: promoType(r) === 'free_bags' ? totalFreeBags(r, brands) : '—' },
                  { label: 'Reason', value: r.reason || '—' },
                ]}
              />
            ))
          )}
        </div>
        <div className={`hidden sm:block ${scrollTableWrap}`}>
          <table className="w-full min-w-[900px] data-table border-separate border-spacing-0 text-left text-sm">
            <thead className={stickyThead}>
              <tr className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className={`whitespace-nowrap px-4 py-3 ${stickyFirstTh}`}>Date</th>
                <th className="px-4 py-3">Customer</th>
                <th className="whitespace-nowrap px-4 py-3">Type</th>
                <th className="whitespace-nowrap px-4 py-3 font-mono">Bill / Invoice</th>
                <th className="whitespace-nowrap px-4 py-3 text-right">Amount</th>
                <th className="whitespace-nowrap px-4 py-3 text-right">Free bags</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-800">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                    No promotions yet. Use &quot;Add promotion&quot; to record a promotion for a customer.
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                    No rows match your search or filters.
                  </td>
                </tr>
              ) : (
                pagedRows.map((r) => {
                  const type = promoType(r);
                  return (
                  <tr
                    key={r.id}
                    {...detailRowAttrs(() => setDetailRow(r), 'hover:bg-slate-50/80')}
                    aria-label={`Promotion ${r.customerName || ''}`}
                  >
                    <td className={`whitespace-nowrap px-4 py-3 tabular-nums ${stickyFirstTd}`}>{r.date}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <p>{r.customerName || '—'}</p>
                      {r.customerId ? (
                        <p className="mt-0.5 font-mono text-xs font-normal text-slate-500">ID {r.customerId}</p>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">{promoTypeLabel(type)}</td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-sm">
                      {r.invoiceNumber || (r.billNumber ? `#${r.billNumber}` : '—')}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums font-medium text-slate-900">
                      {promoAmount(r) > 0 ? money(promoAmount(r)) : '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-semibold tabular-nums text-indigo-800">
                      {type === 'free_bags' ? totalFreeBags(r, brands) : '—'}
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {!loading && rows.length > 0 ? (
          <TablePaginationBar
            page={pagination.page}
            totalPages={pagination.totalPages}
            pageSize={pagination.pageSize}
            totalCount={filteredRows.length}
            onPageChange={pagination.setPage}
            onPageSizeChange={pagination.setPageSize}
          />
        ) : null}
      </div>

      {modalOpen ? (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="promo-modal-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            aria-label="Close"
            onClick={closeModal}
          />
          <div className={`${modalPanelClass} max-h-[90vh] overflow-y-auto`}>
            <h2 id="promo-modal-title" className="text-lg font-bold text-slate-900">
              {editPromotion ? 'Edit promotion' : 'Add promotion'}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Logged in as {getUsername() || '—'}.
              {promoTab === 'free_bags'
                ? ' Free bags reduce live stock; customer is notified by WhatsApp/email.'
                : ' Deducts from customer ledger and cashier; no customer notification.'}
            </p>

            {!editPromotion ? (
              <div className="mt-4 flex flex-wrap gap-1 rounded-xl bg-slate-100 p-1" role="tablist">
                {PROMO_TYPES.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={promoTab === tab.id}
                    onClick={() => switchPromoTab(tab.id)}
                    className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition sm:text-sm ${
                      promoTab === tab.id
                        ? 'bg-white text-indigo-800 shadow-sm ring-1 ring-slate-200'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm font-medium text-indigo-800">{promoTypeLabel(promoTab)}</p>
            )}

            <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
              {saveError ? (
                <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800 ring-1 ring-red-100">{saveError}</p>
              ) : null}
              <label className="block text-sm font-medium text-slate-600">
                Issue date
                <input
                  type="date"
                  required
                  value={form.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  className={filterControl}
                />
              </label>
              <label className="block text-sm font-medium text-slate-600">
                Customer
                <select
                  required
                  value={form.customerId}
                  onChange={(e) => {
                    handleChange('customerId', e.target.value);
                    handleChange('billId', '');
                  }}
                  className={filterControl}
                >
                  <option value="">Select customer</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm font-medium text-slate-600">
                Reason
                <textarea
                  required
                  rows={3}
                  value={form.reason}
                  onChange={(e) => handleChange('reason', e.target.value)}
                  placeholder="e.g. Loyalty reward, target achieved, invoice adjustment…"
                  className={`${filterControl} min-h-[5rem] resize-y`}
                />
              </label>

              {promoTab === 'free_bags' ? (
                <>
                  <label className="block text-sm font-medium text-slate-600">
                    Bill number <span className="font-normal text-slate-400">(optional reference)</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.billNumber}
                      onChange={(e) => handleChange('billNumber', e.target.value)}
                      placeholder="e.g. 001"
                      className={filterControl}
                      maxLength={3}
                    />
                  </label>
                  <div>
                    <p className="text-sm font-medium text-slate-600">Free bags by brand</p>
                    {!stockLoading && !anyStockAvailable ? (
                      <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-amber-100">
                        No bags in stock right now — you cannot issue free bags.
                      </p>
                    ) : null}
                    <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {brands.map((b) => {
                        const available = stockByBrand[b.key] ?? 0;
                        const outOfStock = !stockLoading && available <= 0;
                        return (
                          <label key={b.key} className="block text-xs font-medium text-slate-600">
                            {b.label}
                            <span className="ml-1 font-normal text-slate-400">
                              {stockLoading ? '…' : outOfStock ? 'Out of stock' : `${available.toLocaleString()} in stock`}
                            </span>
                            <input
                              type="number"
                              min={0}
                              step={1}
                              value={form[`${b.key}Bags`]}
                              onChange={(e) => handleChange(`${b.key}Bags`, e.target.value)}
                              className={filterControl}
                              placeholder="0"
                              disabled={stockLoading || outOfStock}
                            />
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </>
              ) : null}

              {promoTab === 'invoice_discount' ? (
                <>
                  <label className="block text-sm font-medium text-slate-600">
                    Invoice
                    <select
                      required
                      value={form.billId}
                      onChange={(e) => handleChange('billId', e.target.value)}
                      className={filterControl}
                      disabled={!form.customerId || billsLoading}
                    >
                      <option value="">
                        {!form.customerId
                          ? 'Select a customer first'
                          : billsLoading
                            ? 'Loading invoices…'
                            : customerBills.length === 0
                              ? 'No invoices for this customer'
                              : 'Select invoice'}
                      </option>
                      {customerBills.map((b) => {
                        const linked = customerInvoiceById.get(b.id);
                        const discounted =
                          linked &&
                          (Number(b.totalAmount) || 0) - (Number(linked.billTotal) || 0) > 0.009;
                        return (
                          <option key={b.id} value={b.id}>
                            {b.date} · {b.invoiceNumber || b.stockId || b.id} ·{' '}
                            {discounted
                              ? `to pay ${money(linked.outstandingAmount)}`
                              : money(b.totalAmount)}
                          </option>
                        );
                      })}
                    </select>
                  </label>
                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium text-slate-600">Discount type</legend>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="radio"
                        name="discountMode"
                        checked={form.discountMode === 'whole_invoice'}
                        onChange={() => handleChange('discountMode', 'whole_invoice')}
                      />
                      Amount for whole invoice (LKR)
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="radio"
                        name="discountMode"
                        checked={form.discountMode === 'per_bag'}
                        onChange={() => handleChange('discountMode', 'per_bag')}
                      />
                      Amount per bag (LKR)
                    </label>
                  </fieldset>
                  <label className="block text-sm font-medium text-slate-600">
                    {form.discountMode === 'per_bag' ? 'Discount per bag (LKR)' : 'Discount for invoice (LKR)'}
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      required
                      value={form.discountValue}
                      onChange={(e) => handleChange('discountValue', e.target.value)}
                      className={filterControl}
                      placeholder="0.00"
                    />
                  </label>
                  {selectedBill && previewDiscount > 0 ? (
                    <div className="rounded-xl bg-emerald-50 px-3 py-2.5 text-sm text-emerald-950 ring-1 ring-emerald-100">
                      <p>Invoice total: {money(selectedBill.totalAmount)}</p>
                      <p>Invoice discount: {money(previewDiscount)}</p>
                      {otherInvoiceDiscount > 0 ? (
                        <p>Already discounted on this invoice: {money(otherInvoiceDiscount)}</p>
                      ) : null}
                      {(Number(customerInvoiceById.get(selectedBill.id)?.paidAmount) || 0) > 0 ? (
                        <p>
                          Already paid: {money(customerInvoiceById.get(selectedBill.id).paidAmount)}
                        </p>
                      ) : null}
                      <p className="mt-1 font-semibold">Amount to be paid: {money(amountToPay)}</p>
                    </div>
                  ) : null}
                </>
              ) : null}

              {promoTab === 'target_promotion' ? (
                <label className="block text-sm font-medium text-slate-600">
                  Promotion amount (LKR)
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    required
                    value={form.discountAmount}
                    onChange={(e) => handleChange('discountAmount', e.target.value)}
                    className={filterControl}
                    placeholder="Reward for target achievement"
                  />
                </label>
              ) : null}

              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  type="submit"
                  disabled={
                    saving ||
                    (promoTab === 'free_bags' && (stockLoading || !anyStockAvailable)) ||
                    (promoTab === 'invoice_discount' && (billsLoading || !form.billId))
                  }
                  className="rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-500/25 transition hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : editPromotion ? 'Save changes' : 'Save promotion'}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {downloadOpen ? (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="promo-download-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            aria-label="Close"
            onClick={() => setDownloadOpen(false)}
          />
          <div className={modalPanelClass}>
            <h2 id="promo-download-title" className="text-lg font-bold text-slate-900">
              Download promotions
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Include all three types, or pick them separately. Search, dates, and the customer filter still apply.
              PDF and Excel download together.
            </p>
            <div className="mt-4 space-y-2">
              <label
                className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm ring-1 ${
                  filteredRows.length === 0
                    ? 'cursor-not-allowed bg-slate-50 text-slate-400 ring-slate-100'
                    : allDownloadTypesSelected
                      ? 'cursor-pointer bg-indigo-50 text-indigo-900 ring-indigo-200'
                      : 'cursor-pointer bg-white text-slate-800 ring-slate-200 hover:bg-slate-50'
                }`}
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={allDownloadTypesSelected}
                    disabled={filteredRows.length === 0}
                    onChange={toggleAllDownloadTypes}
                    className="rounded text-indigo-600 focus:ring-indigo-500/40"
                  />
                  <span className="font-medium">All types</span>
                </span>
                <span className="shrink-0 tabular-nums text-xs text-slate-500">
                  {filteredRows.length} row{filteredRows.length === 1 ? '' : 's'}
                </span>
              </label>
              {PROMO_TYPES.map((type) => {
                const count = filteredRows.filter((row) => promoType(row) === type.id).length;
                const selected = downloadTypes.includes(type.id);
                return (
                  <label
                    key={type.id}
                    className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm ring-1 ${
                      count === 0
                        ? 'cursor-not-allowed bg-slate-50 text-slate-400 ring-slate-100'
                        : selected
                          ? 'cursor-pointer bg-indigo-50 text-indigo-900 ring-indigo-200'
                          : 'cursor-pointer bg-white text-slate-800 ring-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-2.5">
                      <input
                        type="checkbox"
                        checked={selected && count > 0}
                        disabled={count === 0}
                        onChange={() => toggleDownloadType(type.id)}
                        className="rounded text-indigo-600 focus:ring-indigo-500/40"
                      />
                      <span className="font-medium">{type.label}</span>
                    </span>
                    <span className="shrink-0 tabular-nums text-xs text-slate-500">
                      {count} row{count === 1 ? '' : 's'}
                    </span>
                  </label>
                );
              })}
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={confirmDownload}
                disabled={downloadRows.length === 0}
                className="rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-500/25 transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Download PDF + Excel
              </button>
              <button
                type="button"
                onClick={() => setDownloadOpen(false)}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <RowDetailModal
        open={!!detailRow}
        row={detailRow}
        variant="promotion"
        onClose={() => setDetailRow(null)}
        actions={
          detailRow?.id && canEditDetails() ? (
            <div className="mt-4 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => openPromotionEdit(detailRow)}
                className="w-full rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-sm font-semibold text-indigo-800 ring-1 ring-indigo-100 hover:bg-indigo-100"
              >
                Edit promotion
              </button>
              <button
                type="button"
                disabled={deletingId === detailRow.id}
                onClick={() => handleDelete(detailRow)}
                className="w-full rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-800 ring-1 ring-rose-100 hover:bg-rose-100 disabled:opacity-50"
              >
                {deletingId === detailRow.id ? 'Deleting…' : 'Delete promotion'}
              </button>
            </div>
          ) : null
        }
      />
    </div>
  );
}

function PromotionRulesPanel() {
  const { brands, loading: productsLoading } = useBagProducts();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [form, setForm] = useState(() => emptyRuleForm([]));
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');

  const loadCustomers = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/api/customers`);
      if (!res.ok) throw new Error('Failed to load customers');
      const data = await res.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch {
      setCustomers([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/promotion-rules`);
      if (!res.ok) throw new Error('Failed to load promotion rules');
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Could not load data');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    if (!modalOpen) return;
    setForm((f) => {
      const next = { ...f.cashbacks };
      let changed = false;
      for (const b of brands) {
        if (!(b.key in next)) {
          next[b.key] = '';
          changed = true;
        }
      }
      return changed ? { ...f, cashbacks: next } : f;
    });
  }, [modalOpen, brands]);

  const filteredRows = useMemo(() => {
    const items = [];
    for (const r of rows) {
      if (!rulePeriodOverlaps(r.startDate, r.endDate, dateFrom, dateTo)) continue;
      if (customerFilter && r.customerId !== customerFilter) continue;
      const entries = ruleCashbackEntries(r, brands);
      for (const e of entries) {
        items.push({
          rowKey: `${r.id}-${e.key}`,
          rule: r,
          productKey: e.key,
          productLabel: e.label,
          amount: e.amount,
        });
      }
    }
    return items.filter((item) =>
      rowMatchesQuery(search, [
        item.rule.customerName,
        item.rule.customerId,
        item.rule.startDate,
        item.rule.endDate,
        item.rule.enteredBy,
        item.productLabel,
        String(item.amount),
      ]),
    );
  }, [rows, search, dateFrom, dateTo, customerFilter, brands]);

  const pagination = useTablePagination(filteredRows.length, [search, dateFrom, dateTo, customerFilter]);
  const pagedRows = useMemo(
    () => withRuleRowSpans(filteredRows.slice(pagination.offset, pagination.offset + pagination.pageSize)),
    [filteredRows, pagination.offset, pagination.pageSize],
  );

  const openModal = () => {
    setEditRule(null);
    setForm(emptyRuleForm(brands));
    setSaveError(null);
    loadCustomers();
    setModalOpen(true);
  };

  const openRuleEdit = (rule) => {
    if (!rule?.id) return;
    setSaveError(null);
    loadCustomers();
    setEditRule(rule);
    setForm(formFromRule(rule, brands));
    setDetailRow(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditRule(null);
    setSaveError(null);
  };

  const handleCashbackChange = (key, value) => {
    setForm((f) => ({
      ...f,
      cashbacks: { ...f.cashbacks, [key]: value },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const username = getUsername();
    if (!username) {
      setSaveError('You need to be signed in with a username.');
      return;
    }
    if (!form.customerId) {
      setSaveError('Select a customer.');
      return;
    }
    if (!form.startDate || !form.endDate) {
      setSaveError('Select a start date and end date.');
      return;
    }
    if (form.endDate < form.startDate) {
      setSaveError('End date must be on or after start date.');
      return;
    }
    const cashbacks = {};
    let anyPositive = false;
    for (const b of brands) {
      const amount = Number(form.cashbacks[b.key]);
      const n = Number.isFinite(amount) && amount > 0 ? amount : 0;
      cashbacks[b.key] = n;
      if (n > 0) anyPositive = true;
    }
    if (!anyPositive) {
      setSaveError('Enter a cashback amount for at least one product.');
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        customerId: form.customerId,
        startDate: form.startDate,
        endDate: form.endDate,
        cashbacks,
      };
      const isEdit = !!editRule?.id;
      const res = await fetch(
        isEdit
          ? `${apiBase}/api/promotion-rules/${encodeURIComponent(editRule.id)}`
          : `${apiBase}/api/promotion-rules`,
        {
          method: isEdit ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            isEdit ? { ...payload, updatedBy: username } : { ...payload, enteredBy: username },
          ),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveError(data.error || 'Save failed');
        return;
      }
      await load();
      closeModal();
    } catch {
      setSaveError('Could not reach the server.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    const customer = customers.find((c) => c.id === customerFilter)?.name || '';
    downloadPromotionRules(filteredRows, {
      dateFrom,
      dateTo,
      customer,
      search,
    });
  };

  const handleDelete = async (rule) => {
    if (!rule?.id) return;
    const username = getUsername();
    if (!username) {
      alert('You need to be signed in.');
      return;
    }
    const label = rule.customerName ? ` for ${rule.customerName}` : '';
    if (!window.confirm(`Delete this promotion rule${label}?`)) return;
    setDeletingId(rule.id);
    try {
      const res = await fetch(`${apiBase}/api/promotion-rules/${encodeURIComponent(rule.id)}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Could not delete the rule.');
        return;
      }
      setDetailRow(null);
      await load();
    } catch {
      alert('Could not reach the server.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-500">
          Set per-product cashback amounts for a customer over a date range.
        </p>
        <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row">
          <button
            type="button"
            onClick={handleDownload}
            disabled={loading || filteredRows.length === 0}
            className="inline-flex w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >
            Download (PDF + Excel)
          </button>
          <button
            type="button"
            onClick={openModal}
            className="inline-flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition hover:brightness-[1.03] sm:w-auto"
          >
            Add a rule
          </button>
        </div>
      </div>

      {error ? (
        <p className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-800 ring-1 ring-red-100" role="alert">
          {error}
        </p>
      ) : null}

      <TableFiltersBar
        hint={
          !loading && rows.length > 0
            ? `Showing ${filteredRows.length} product row${filteredRows.length === 1 ? '' : 's'}`
            : null
        }
      >
        <label className={filterLabel}>
          Search
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Customer, product, amount…"
            className={filterControl}
          />
        </label>
        <label className={filterLabelNarrow}>
          From date
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className={filterControl}
          />
        </label>
        <label className={filterLabelNarrow}>
          To date
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className={filterControl}
          />
        </label>
        <label className={filterLabel}>
          Customer
          <select
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            className={filterControl}
          >
            <option value="">All customers</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </TableFiltersBar>

      <div className="space-y-3">
        <div className={mobileCardList}>
          {loading ? (
            <p className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-slate-500 ring-1 ring-slate-100">
              <LoadingSpinner />
            </p>
          ) : rows.length === 0 ? (
            <p className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-slate-500 ring-1 ring-slate-100">
              No promotion rules yet. Use &quot;Add a rule&quot; to set cashback amounts for a customer.
            </p>
          ) : filteredRows.length === 0 ? (
            <p className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-slate-500 ring-1 ring-slate-100">
              No rows match your search or filters.
            </p>
          ) : (
            pagedRows.map((item) => (
              <MobileRowCard
                key={item.rowKey}
                title={item.rule.customerName || '—'}
                subtitle={[
                  item.rule.customerId ? `ID ${item.rule.customerId}` : '',
                  `${item.rule.startDate || '—'} – ${item.rule.endDate || '—'}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                onClick={() => setDetailRow(item.rule)}
                fields={[
                  { label: 'Product', value: item.productLabel },
                  { label: 'Cashback', value: money(item.amount) },
                ]}
              />
            ))
          )}
        </div>
        <div className={`hidden sm:block ${scrollTableWrap}`}>
          <table className="w-full min-w-[800px] data-table border-separate border-spacing-0 text-left text-sm">
            <thead className={stickyThead}>
              <tr className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className={`whitespace-nowrap px-4 py-3 ${stickyFirstTh}`}>Customer</th>
                <th className="whitespace-nowrap px-4 py-3">Start date</th>
                <th className="whitespace-nowrap px-4 py-3">End date</th>
                <th className="px-4 py-3">Product</th>
                <th className="whitespace-nowrap px-4 py-3 text-right">Cashback</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-800">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                    No promotion rules yet. Use &quot;Add a rule&quot; to set cashback amounts for a customer.
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                    No rows match your search or filters.
                  </td>
                </tr>
              ) : (
                pagedRows.map((item) => (
                  <tr
                    key={item.rowKey}
                    {...detailRowAttrs(() => setDetailRow(item.rule), 'hover:bg-slate-50/80')}
                    aria-label={`Promotion rule ${item.rule.customerName || ''} ${item.productLabel}`}
                  >
                    {item.ruleRowSpan > 0 ? (
                      <td
                        rowSpan={item.ruleRowSpan}
                        className={`px-4 py-3 align-middle font-medium text-slate-900 ${stickyFirstTd}`}
                      >
                        <p>{item.rule.customerName || '—'}</p>
                        {item.rule.customerId ? (
                          <p className="mt-0.5 font-mono text-xs font-normal text-slate-500">
                            ID {item.rule.customerId}
                          </p>
                        ) : null}
                      </td>
                    ) : null}
                    {item.ruleRowSpan > 0 ? (
                      <td rowSpan={item.ruleRowSpan} className="whitespace-nowrap px-4 py-3 align-middle tabular-nums">
                        {item.rule.startDate || '—'}
                      </td>
                    ) : null}
                    {item.ruleRowSpan > 0 ? (
                      <td rowSpan={item.ruleRowSpan} className="whitespace-nowrap px-4 py-3 align-middle tabular-nums">
                        {item.rule.endDate || '—'}
                      </td>
                    ) : null}
                    <td className="px-4 py-3 text-slate-800">{item.productLabel}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-semibold tabular-nums text-indigo-800">
                      {money(item.amount)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!loading && rows.length > 0 ? (
          <TablePaginationBar
            page={pagination.page}
            totalPages={pagination.totalPages}
            pageSize={pagination.pageSize}
            totalCount={filteredRows.length}
            onPageChange={pagination.setPage}
            onPageSizeChange={pagination.setPageSize}
          />
        ) : null}
      </div>

      {modalOpen ? (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="promo-rule-modal-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            aria-label="Close"
            onClick={closeModal}
          />
          <div className={`${modalPanelClass} max-h-[90vh] overflow-y-auto`}>
            <h2 id="promo-rule-modal-title" className="text-lg font-bold text-slate-900">
              {editRule ? 'Edit promotion rule' : 'Add a rule'}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Logged in as {getUsername() || '—'}. Choose a customer, period, and cashback per product.
            </p>
            <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
              {saveError ? (
                <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800 ring-1 ring-red-100">{saveError}</p>
              ) : null}
              <label className="block text-sm font-medium text-slate-600">
                Customer
                <select
                  required
                  value={form.customerId}
                  onChange={(e) => setForm((f) => ({ ...f, customerId: e.target.value }))}
                  className={filterControl}
                >
                  <option value="">Select customer</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block text-sm font-medium text-slate-600">
                  Start date
                  <input
                    type="date"
                    required
                    value={form.startDate}
                    onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                    className={filterControl}
                  />
                </label>
                <label className="block text-sm font-medium text-slate-600">
                  End date
                  <input
                    type="date"
                    required
                    value={form.endDate}
                    min={form.startDate || undefined}
                    onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                    className={filterControl}
                  />
                </label>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600">Cashback by product (LKR)</p>
                {productsLoading ? (
                  <p className="mt-2 text-sm text-slate-500">
                    <LoadingSpinner />
                  </p>
                ) : brands.length === 0 ? (
                  <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-amber-100">
                    No products in the catalog yet. Add products in Shop first.
                  </p>
                ) : (
                  <div className="mt-2 divide-y divide-slate-100 overflow-hidden rounded-xl ring-1 ring-slate-200">
                    {brands.map((b) => (
                      <label
                        key={b.key}
                        className="flex items-center justify-between gap-3 bg-white px-3 py-2.5 text-sm"
                      >
                        <span className="min-w-0 font-medium text-slate-800">
                          {formatBrandLabel(b) || b.label}
                        </span>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={form.cashbacks[b.key] ?? ''}
                          onChange={(e) => handleCashbackChange(b.key, e.target.value)}
                          className="mt-0 w-28 shrink-0 rounded-xl border-0 bg-slate-100 px-3 py-2 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                          placeholder="0.00"
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  type="submit"
                  disabled={saving || productsLoading || brands.length === 0}
                  className="rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-500/25 transition hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : editRule ? 'Save changes' : 'Save rule'}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <RowDetailModal
        open={!!detailRow}
        row={detailRow}
        variant="promotionRule"
        onClose={() => setDetailRow(null)}
        actions={
          detailRow?.id && canEditDetails() ? (
            <div className="mt-4 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => openRuleEdit(detailRow)}
                className="w-full rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-sm font-semibold text-indigo-800 ring-1 ring-indigo-100 hover:bg-indigo-100"
              >
                Edit rule
              </button>
              <button
                type="button"
                disabled={deletingId === detailRow.id}
                onClick={() => handleDelete(detailRow)}
                className="w-full rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-800 ring-1 ring-rose-100 hover:bg-rose-100 disabled:opacity-50"
              >
                {deletingId === detailRow.id ? 'Deleting…' : 'Delete rule'}
              </button>
            </div>
          ) : null
        }
      />
    </div>
  );
}

export default function PromotionsPage() {
  const [tab, setTab] = useState('special');

  return (
    <div className="space-y-5">
      <div
        className="rounded-[20px] bg-white p-2 shadow-lg shadow-slate-200/40 ring-1 ring-slate-100 sm:p-2.5"
        role="tablist"
        aria-label="Promotion sections"
      >
        <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
          {PAGE_TABS.map(({ id, label }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(id)}
                className={`rounded-xl py-3.5 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 sm:py-4 sm:text-base ${
                  active
                    ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'special' ? <SpecialPromotionsPanel /> : null}
      {tab === 'rules' ? <PromotionRulesPanel /> : null}
    </div>
  );
}
